// Read-only sibling of the panorama Gantt: one row per worksite, coloured by status.

import React, { useMemo } from "react"
import EmptyState from "@react/shared/ui/EmptyState"
import { toDate, todayUTC, daysBetween, fmtISO, fmtShort, snapAxis, monthColumns, monthLabelEvery, ROW_HOVER_STYLE, useContainerWidth } from "../../shared/timeline"

const LABEL_W     = 340
const ROW_H       = 32
const HEADER_H    = 30
const PAD_X       = 16
const PAD_BOTTOM  = 26
const MIN_DAY_W   = 1     // read-only overview: a week is still 7px wide
const DOT_R       = 5
const STROKE      = 3
const HEAD_LEN    = 9
const MIN_ARROW_W = DOT_R + HEAD_LEN + 4

const STATUS_COLORS = {
  draft:     "#38bdf8",   // sky-400: not started yet
  active:    "#1e3a8a",   // blue-900
  on_hold:   "#d97706",   // amber-600
  reception: "#059669",   // emerald-600
  completed: "#94a3b8",   // slate-400: clearly lighter than the navy of running sites
  cancelled: "#cbd5e1",   // slate-300
}
const GRID = "#e2e8f0", GRID_TEXT = "#64748b", TODAY = "#0f172a", ROW_HOVER = "#f8fafc"

const ProjectsTimeline = ({ projects }) => {
  const [scrollRef, containerW] = useContainerWidth()

  const dated = useMemo(() => projects.filter(project => project.start_date), [projects])
  const axis  = useMemo(() => snapAxis([todayUTC(), ...dated.flatMap(project => [toDate(project.start_date), toDate(project.end_date)].filter(Boolean))], 90), [dated])
  const dayW  = Math.max(MIN_DAY_W, (containerW - LABEL_W - PAD_X * 2) / axis.days)
  const xFromDate = date => PAD_X + LABEL_W + daysBetween(axis.start, toDate(date)) * dayW
  const totalW = Math.max(containerW, PAD_X * 2 + LABEL_W + axis.days * dayW)
  const totalH = HEADER_H + dated.length * ROW_H + PAD_BOTTOM

  const months     = useMemo(() => monthColumns(axis, dayW, xFromDate), [axis, dayW])
  const labelEvery = monthLabelEvery(dayW)
  const todayX = xFromDate(fmtISO(todayUTC()))

  const statusesShown = useMemo(() => [...new Set(dated.map(project => project.status))], [dated])

  if (dated.length === 0) {
    return <EmptyState size="lg">{I18n.t("projects.index.timeline.empty")}</EmptyState>
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11.5px] text-slate-500">
        {statusesShown.map(status => (
          <span key={status} className="inline-flex items-center gap-1.5">
            <svg width="30" height="12" aria-hidden="true">
              <line x1="5" y1="6" x2="23" y2="6" stroke={STATUS_COLORS[status] || STATUS_COLORS.draft} strokeWidth="3" />
              <path d="M30 6 L22 2 L22 10 Z" fill={STATUS_COLORS[status] || STATUS_COLORS.draft} />
              <circle cx="5" cy="6" r="4" fill={STATUS_COLORS[status] || STATUS_COLORS.draft} />
            </svg>
            {dated.find(project => project.status === status)?.status_label || status}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-[2px]" style={{ background: TODAY }} />{I18n.t("projects.gantt.today")}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div ref={scrollRef} className="overflow-x-auto">
          <svg width={totalW} height={totalH} style={{ display: "block", fontFamily: "inherit" }}>
            <style>{`
              .pt-row { cursor: pointer; }
              .pt-row__bg { opacity: 0; transition: opacity .12s; }
              .pt-row:hover .pt-row__bg { opacity: 1; }
              .pt-row:hover .pt-row__name { fill: #1d4ed8; }
            `}</style>

            {months.map((month, index) => (
              <g key={index}>
                <line x1={month.x} y1={HEADER_H - 8} x2={month.x} y2={totalH - PAD_BOTTOM + 6} stroke={GRID} strokeWidth="1" />
                {index % labelEvery === 0 && (
                  <text x={month.x + 5} y={15} fill={GRID_TEXT} fontSize="11" fontWeight="500">
                    {month.label}{month.year ? ` ${month.year}` : ""}
                  </text>
                )}
              </g>
            ))}

            <line x1={todayX} y1={HEADER_H - 8} x2={todayX} y2={totalH - PAD_BOTTOM + 6} stroke={TODAY} strokeWidth="1.5" />
            <text x={todayX} y={totalH - 7} textAnchor="middle" fill={TODAY} fontSize="10.5" fontWeight="600">{I18n.t("projects.gantt.today")}</text>

            {dated.map((project, index) => {
              const y     = HEADER_H + index * ROW_H + ROW_H / 2
              const color = STATUS_COLORS[project.status] || STATUS_COLORS.draft
              const x1    = xFromDate(project.start_date)
              const x2    = project.end_date ? xFromDate(project.end_date) : null
              const arrow = x2 != null && x2 - x1 >= MIN_ARROW_W
              const maxChars = Math.floor((LABEL_W - 12) / 6.4)
              const label = `${project.code} · ${project.name}`
              return (
                <g key={project.id} className="pt-row" onClick={() => { if (project.panorama_url) window.location.href = project.panorama_url }}>
                  <title>{`${project.name}\n${fmtShort(project.start_date)} → ${fmtShort(project.end_date) || "…"} · ${project.status_label}`}</title>
                  <rect className="pt-row__bg" x={0} y={HEADER_H + index * ROW_H} width={totalW} height={ROW_H} fill={ROW_HOVER} style={ROW_HOVER_STYLE} />
                  <text className="pt-row__name" x={PAD_X} y={y + 4} fill="#0f172a" fontSize="12.5" fontWeight="500">
                    {label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label}
                  </text>
                  {arrow && (
                    <>
                      <line x1={x1} y1={y} x2={x2 - HEAD_LEN + 1} y2={y} stroke={color} strokeWidth={STROKE} />
                      <path d={`M${x2} ${y} L${x2 - HEAD_LEN} ${y - 5} L${x2 - HEAD_LEN} ${y + 5} Z`} fill={color} />
                    </>
                  )}
                  <circle cx={x1} cy={y} r={DOT_R} fill={color} />
                </g>
              )
            })}
          </svg>
        </div>
      </div>
    </div>
  )
}

export default ProjectsTimeline
